/**
 * Per-theme Protomaps basemap flavors.
 *
 * Each flavor spreads a Protomaps named base (`dark` for the three dark themes,
 * `light` for `defaulted`) and overrides only the palette-carrying fields with
 * the theme's own tokens (rgb triplets from `apps/web/src/styles/tokens.css`),
 * so the vector basemap reads as a native part of the active dashboard theme.
 *
 * The surface family gets three distinguishable steps: the map background, the
 * land (`earth`) as the theme's card surface, and water a step darker (dark
 * themes) or lighter (`defaulted`) than land. Roads read against the land tone
 * via muted-to-foreground mixes with border-token casings; only highways carry
 * the theme's primary so the accent never competes with session markers.
 * Parks/wood/scrub are a low-saturation tint of the theme's primary over the
 * land tone, not Protomaps' greens. All other flavor fields (POIs, sand, beach,
 * glacier, tunnels, bridges, fonts) stay from the Protomaps base.
 */

import { namedFlavor, type Flavor } from "@protomaps/basemaps";
import type { ThemeName } from "../../components/theme/ThemeProvider";

type Palette = {
  bg: string;
  card: string;
  muted: string;
  border: string;
  fg: string;
  primary: string;
};

/** Theme tokens, mirrored from tokens.css as `rgb(r, g, b)` strings. */
const PALETTES: Record<ThemeName, Palette> = {
  spectre: {
    bg: "rgb(6, 8, 24)",
    card: "rgb(14, 23, 38)",
    muted: "rgb(24, 30, 46)",
    border: "rgb(27, 46, 75)",
    fg: "rgb(224, 230, 237)",
    primary: "rgb(38, 174, 127)",
  },
  smoked: {
    bg: "rgb(22, 28, 35)",
    card: "rgb(29, 37, 48)",
    muted: "rgb(23, 30, 39)",
    border: "rgb(47, 60, 75)",
    fg: "rgb(224, 230, 237)",
    primary: "rgb(67, 97, 238)",
  },
  reel: {
    bg: "rgb(24, 22, 20)",
    card: "rgb(34, 32, 30)",
    muted: "rgb(42, 40, 38)",
    border: "rgb(52, 48, 44)",
    fg: "rgb(227, 224, 219)",
    primary: "rgb(240, 165, 0)",
  },
  defaulted: {
    bg: "rgb(241, 242, 243)",
    card: "rgb(255, 255, 255)",
    muted: "rgb(235, 237, 242)",
    border: "rgb(211, 211, 211)",
    fg: "rgb(81, 83, 101)",
    primary: "rgb(67, 97, 238)",
  },
};

/** Which Protomaps named base each theme spreads from. */
const BASE: Record<ThemeName, "dark" | "light"> = {
  spectre: "dark",
  smoked: "dark",
  reel: "dark",
  defaulted: "light",
};

// How far each theme's highway is blended FROM the neutral `roadMajor` TOWARD the
// accent `primary` (0 = neutral road, 1 = full accent). Kept in 0.45-0.6 so the
// highway reads as accent-tinted yet subdued — clearly below marker prominence in
// dense cities. The brightest accents (reel amber, defaulted blue) get the lowest
// blend so they do not compete with the markers.
const HIGHWAY_MIX: Record<ThemeName, number> = {
  reel: 0.45,
  defaulted: 0.5,
  smoked: 0.55,
  spectre: 0.6,
};

/** Parse `rgb(r, g, b)` to a numeric triplet. */
const rgb = (s: string): [number, number, number] => {
  const m = s.match(/\d+/g)?.map(Number) ?? [0, 0, 0];
  return [m[0] ?? 0, m[1] ?? 0, m[2] ?? 0];
};

/** Linear blend of two `rgb(...)` strings; `t` in [0,1] toward `b`. */
const mix = (a: string, b: string, t: number): string => {
  const [ar, ag, ab] = rgb(a);
  const [br, bg, bb] = rgb(b);
  const c = (x: number, y: number) => Math.round(x + (y - x) * t);
  return `rgb(${c(ar, br)}, ${c(ag, bg)}, ${c(ab, bb)})`;
};

const buildFlavor = (p: Palette, base: "dark" | "light", highwayMix: number): Flavor => {
  const isLight = base === "light";

  // Surface family — three distinguishable steps. Land is the card tone so the
  // map reads as an extension of the panel; water contrasts against it (darker
  // on dark themes, lighter on `defaulted`); the map background sits between.
  const land = isLight ? p.muted : p.card;
  const water = isLight ? p.card : p.bg;
  const mapBg = isLight ? p.bg : mix(p.bg, p.card, 0.5);

  // Roads read against the land tone via muted-to-foreground mixes; casings use
  // the border token. Minor is a subtle step, major (arterial) a brighter one;
  // only highways carry the theme primary.
  const roadMinor = mix(land, p.fg, 0.3);
  const roadMajor = mix(land, p.fg, 0.55);
  // Highway: accent-tinted but subdued — blended from the neutral major-road tone
  // toward the theme primary, so it stays clearly below marker prominence.
  const highway = mix(roadMajor, p.primary, highwayMix);

  // Parks/wood/scrub — a low-saturation tint of the primary over the land tone,
  // never Protomaps' greens (on `reel` this reads warm-gold, not green).
  const greenA = mix(land, p.primary, 0.16);
  const greenB = mix(land, p.primary, 0.24);

  // Low-zoom landcover object (grassland/forest/scrub/farmland/barren/urban_area/
  // glacier). Left at the Protomaps default it shows base mint greens at world
  // zoom; tint it into the theme's surface family so low-zoom land reads on-palette:
  // vegetation is a subtle primary tint over land, built-up a faint step, ice a
  // light neutral (near-white on `defaulted`, a light cool step on the dark themes).
  const lightNeutral = isLight ? "rgb(250, 250, 252)" : mix(land, p.fg, 0.45);

  return {
    ...namedFlavor(base),

    // Surfaces
    background: mapBg,
    earth: land,
    water,

    // Landuse polygons (subtle primary tint, not base greens)
    park_a: greenA,
    park_b: greenB,
    wood_a: greenA,
    wood_b: greenB,
    scrub_a: greenA,
    scrub_b: greenB,

    // Low-zoom landcover object — tinted into the surface family (see above)
    landcover: {
      ...namedFlavor(base).landcover,
      grassland: mix(land, p.primary, 0.12),
      forest: mix(land, p.primary, 0.18),
      scrub: mix(land, p.primary, 0.1),
      farmland: mix(land, p.primary, 0.07),
      barren: land,
      urban_area: mix(land, p.fg, 0.05),
      glacier: lightNeutral,
    },

    // Structure lines
    boundaries: p.border,
    railway: p.border,
    buildings: p.border,

    // Road fills — muted steps for the network, primary reserved for highways
    minor_a: roadMinor,
    minor_b: roadMinor,
    minor_service: roadMinor,
    link: roadMinor,
    other: roadMinor,
    major: roadMajor,
    highway,

    // Tunnels/bridges follow the same road tones so a city's grade-separated
    // segments do not pop in a foreign Protomaps hue.
    tunnel_highway: highway,
    bridges_highway: highway,
    tunnel_major: roadMajor,
    bridges_major: roadMajor,

    // Road casings — border token
    minor_casing: p.border,
    minor_service_casing: p.border,
    link_casing: p.border,
    major_casing_early: p.border,
    major_casing_late: p.border,
    highway_casing_early: p.border,
    highway_casing_late: p.border,

    // Labels — foreground text with a background-token halo, legible at 375px
    city_label: p.fg,
    state_label: p.fg,
    country_label: p.fg,
    subplace_label: p.fg,
    address_label: p.fg,
    roads_label_major: p.fg,
    roads_label_minor: p.fg,
    ocean_label: p.fg,
    roads_label_minor_halo: p.bg,
    roads_label_major_halo: p.bg,
    subplace_label_halo: p.bg,
    city_label_halo: p.bg,
    state_label_halo: p.bg,
    address_label_halo: p.bg,
  };
};

export const FLAVORS: Record<ThemeName, Flavor> = {
  spectre: buildFlavor(PALETTES.spectre, BASE.spectre, HIGHWAY_MIX.spectre),
  smoked: buildFlavor(PALETTES.smoked, BASE.smoked, HIGHWAY_MIX.smoked),
  reel: buildFlavor(PALETTES.reel, BASE.reel, HIGHWAY_MIX.reel),
  defaulted: buildFlavor(PALETTES.defaulted, BASE.defaulted, HIGHWAY_MIX.defaulted),
};
